/* eslint-disable */
/**
 * @author Leonel Martinez
 * @module Vehicle
 * @fileoverview `Vuex vehicle module`
 */
import Vue from "vue";
import { vehicleService } from "@/services";
import { MESSAGES, router, ROUTES } from "@/utils";
import { rootAction } from '../index'

/**
 * @description Looks at the LocalStorage for VUE_APP_LOCALSTORAGE_DATA
 * @see .env files
 */

const storage = JSON.parse(
  localStorage.getItem(process.env.VUE_APP_LOCALSTORAGE_DATA)
);

// Validates that yearsList did not filled with an error response
// This should not happened if errors are validated when filling yearstList
if (!Array.isArray(storage?.years)) {
  delete storage?.years;
  localStorage.setItem(process.env.VUE_APP_LOCALSTORAGE_DATA, storage);
}

class Vehicle {
  constructor() {
    this.id = 0,
      this.date,
      this.ymme = {
        year: null,
        make: null,
        makeCode: null,
        model: null,
        modelCode: null,
        engine: null,
        engineCode: null
      },
      this.color = null,
      this.licensePlate = null,
      this.vin = null,
      this.description = null,
      this.owner = null,
      this.tag = null,
      this.fleet = null,
      this.fleetNumber = null,
      this.odometerMileage = null,
      this.mileageUnit = null,
      this.keepForever = null
  }
};

export const vehicle = {
  namespaced: true,
  state: {
    years: storage?.years || [],
    makes: [],
    models: [],
    engines: [],
    vehicles: storage?.vehicles || [],
    selectedVehicle: storage?.selectedVehicle || new Vehicle(),
    minYears: 1969,
    maxYears: 2020
  },
  getters: {
    /**
     * @returns {boolean}
     */
    isVehicleSelected: state => !!state.selectedVehicle.id,
    /**
     * @returns {object} A format vehicle for browing on the catalog.
     */
    isAnalyticsAvailable: state => state.analytics.labels.length > 0,
    vehicleForInquiry: state => ({
      id: state.selectedVehicle.id,
      ymme: state.selectedVehicle.ymme
    }),
    vehicleForMfgAlllines: state => ({
      year: state.selectedVehicle.ymme.year,
      makeCode: state.selectedVehicle.ymme.makeCode,
      modelCode: state.selectedVehicle.ymme.modelCode,
      engineCode: state.selectedVehicle.ymme.engineCode
    }),
    /**
     * @returns {string} The full vehicle name.
     * Note that the properties are chained using an OR || followed by an empty object notation.
     * This technique allows the browser to optionally chain and get rid of undefined values.
     */
    selectedVehicleText: state => {
      return `
        ${state.selectedVehicle?.ymme?.vin || ''}
        ${state.selectedVehicle?.ymme?.year}
        ${state.selectedVehicle?.ymme?.make}
        ${state.selectedVehicle?.ymme?.model}
        ${state.selectedVehicle?.ymme?.engine}
        `;
    }
  },
  actions: {
    /**
     * @description Set the selected vehicle for global purposes.
     * @param vehicle
     */
    SET_VEHICLE: ({ dispatch, commit }, vehicle) => {
      dispatch("parts/RESET_SPECIFIC_ANSWERS", {}, { root: true });
      dispatch("parts/RESET_PARTS_LOOKUP", {}, { root: true });
      commit("UPDATE_STATE", {
        prop: "selectedVehicle",
        value: vehicle
      });
    },
    /**
     * @description GET Service for all related YMME APIs.
     * @param {string} service
     * @param {object} params
     */

    GET_YMME_DATA: async ({ dispatch, commit }, { service, params }) => {
      const response = await vehicleService.getData(service, params);

      if (response.statusText != "OK") {
        dispatch("snackbar/SHOW_SNACKBAR", MESSAGES.ERROR[service.toUpperCase()], { root: true });
        return response;
      }

      switch (service) {
        case 'vehicles': /* VEHICLES */
          response.data.forEach(vehicle =>
            vehicle["fulltext"] = `
              ${vehicle?.ymme?.year}
              ${vehicle?.ymme?.make}
              ${vehicle?.ymme?.model}
              ${vehicle?.ymme?.engine}`
              .replace(/\s+/g, " ")
              .replace(/\r?\n|\r/, "")
          );
          break;
        default:
          break;
      }

      commit("UPDATE_STATE", {
        prop: service,
        value: response.data
      });

      return response;
    },
    /**
     * @description Creates a new vehicle based on the YMME.
     * @param {object} vehicle Object
     * @returns {Promise} with an object of the persited vehicle with `id` and `date`.
     */
    CREATE_VEHICLE: async ({ dispatch, commit }, { vehicle, vin = "" }) => {
      dispatch("parts/RESET_SPECIFIC_ANSWERS", {}, { root: true });
      dispatch("parts/RESET_PARTS_LOOKUP", {}, { root: true });

      // if (vehicle && vin)
      //   vehicle.vin = vin;

      let response = await vehicleService.create(vehicle, vin );

      if (response.statusText != "OK") {
        const message = response.statusText == 'YMME PROP ERROR'
          ? MESSAGES.VEHICLE_DIRTY
          : MESSAGES.ERROR.CREATE_VEHICLE;

        dispatch("snackbar/SHOW_SNACKBAR", message, rootAction);
        return;
      }

      const created_vehicle = response.data;
      // created_vehicle.ymme.vin = vin;

      commit("UPDATE_STATE", {
        prop: "selectedVehicle",
        value: created_vehicle
      });

      dispatch("snackbar/SHOW_SNACKBAR", MESSAGES.VEHICLE_CREATED, rootAction);
    },
    /**
     * @description Updates the vehicle based on the `id`
     * @returns {void} Void.
     */
    UPDATE_VEHICLE: ({ dispatch, state }) =>
      new Promise(resolve => {
        vehicleService.update(state.selectedVehicle).then(
          () => {
            dispatch("snackbar/SHOW_SNACKBAR", MESSAGES.VEHICLE_UPDATED, rootAction);
            resolve(true);
          },
          error => {
            dispatch("snackbar/SHOW_SNACKBAR", MESSAGES.SERVER_ERROR, rootAction);
            reject(error);
          }
        );
      }),
    /**
     * @description Set the selectedVehicle to its initial state.
     */
    RESET_VEHICLE: ({ commit }) => {
      commit("UPDATE_STATE", {
        prop: "selectedVehicle",
        value: new Vehicle()
      });
    },
    /**
     * @desription Updates the vehicle info.
     * @param {string} formField
     * @param {string} value
     */
    UPDATE_FORM_FIELD: ({ commit }, { field, value }) => {
      commit("UPDATE_FORM_FIELD", { field, value });
    },
    /**
     * @desription Updates an specific state model through `prop` param
     * @param {string} prop
     * @param {string} value
     */
    UPDATE_STATE: ({ commit }, { prop, value }) => {
      commit("UPDATE_STATE", { prop, value });
    }
  },
  mutations: {
    SET_VIN: (state, vin) => state.vin = vin,
    /**
     * @description Updates the a form field. If it doesn't exist, create it on the fly.
     * @param {string} field String
     * @param {string} value String | Number
     */
    UPDATE_FORM_FIELD: (state, { field, value }) => {
      !state.selectedVehicle[field]
        ? Vue.set(state.selectedVehicle, field, value)
        : (state.selectedVehicle[field] = value);
    },
    /**
     * @description Updates a unique state property
     * @param {string} prop
     * @param {string} value
     */
    UPDATE_STATE: (state, { prop, value }) => state[prop] = value,
    /**
     * @description Sets a vehicle
     * @param {object} vehicle
     */
    SET_VEHICLE: (state, vehicle) => {
      // First make sure to clean up the selectedVechile
      state.selectedVehicle = new Vehicle();
      state.selectedVehicle.id = vehicle.id;
      state.selectedVehicle.date = vehicle.date;
      state.selectedVehicle.ymme.year = vehicle.ymme.year;
      state.selectedVehicle.ymme.make = vehicle.ymme.make;
      state.selectedVehicle.ymme.makeCode = vehicle.ymme.makeCode;
      state.selectedVehicle.ymme.model = vehicle.ymme.model;
      state.selectedVehicle.ymme.modelCode = vehicle.ymme.modelCode;
      state.selectedVehicle.ymme.engine = vehicle.ymme.engine;
      state.selectedVehicle.ymme.engineCode = vehicle.ymme.engineCode;
      state.selectedVehicle.vin = vehicle.vin;
      state.selectedVehicle.color = vehicle.color;
      state.selectedVehicle.licensePlate = vehicle.licensePlate;
      state.selectedVehicle.description = vehicle.description;
      state.selectedVehicle.owner = vehicle.owner;
      state.selectedVehicle.tag = vehicle.tag;
      state.selectedVehicle.fleetNumber = vehicle.fleetNumber;
      state.selectedVehicle.fleet = vehicle.fleet;
      state.selectedVehicle.keepForever = vehicle.keepForever;
      state.selectedVehicle.odometerMileage = vehicle.odometerMileage;
      state.selectedVehicle.mileageUnit = vehicle.mileageUnit;
    }
  }
};